"use client";

import React from "react";
import { ArrowUpRight, BookOpen, CircleAlert as AlertCircle } from 'lucide-react';
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { AspectRatio } from "@/components/ui/aspect-ratio";

// Fallback metadata for designated guides if remote posts are being indexed or have empty excerpts
const FALLBACK_POSTS = [{
  id: "606",
  title: "How Much Does a Commercial Mechanical Bull Cost in 2026?",
  excerpt: "Breakdown of equipment investment, standard motor specs, commercial insurance rates, and total cost of ownership for venue operators.",
  link: "/MechanicalBullPriceGuidePage",
  featuredImage: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1799866/img/mechanical_bull_cost_guide.png"
}, {
  id: "612",
  title: "How to Start a Mechanical Bull Rental Business in 2026",
  excerpt: "Practical step-by-step guidance on commercial licensing, trailer transport, corporate event pricing models, and client liability contracts.",
  link: "/MechanicalBullBuyingGuidePage",
  featuredImage: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1799866/img/mechanical_bull_rental_business.png"
}, {
  id: "614",
  title: "Commercial Mechanical Bull Safety & Setup Checklist",
  excerpt: "Essential electrical amperage requirements, auto-stop sensor calibration, inflatable landing clearance protocols, and daily operator logs.",
  link: "/SetupSafetyAndMaintenancePage",
  featuredImage: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1799866/img/mechanical_bull_safety_specs.png"
}];
function cleanExcerpt(text?: string): string {
  if (!text) return "";
  const stripped = text.replace(/<[^>]+>/g, "").trim();
  if (stripped.length > 140) {
    return stripped.slice(0, 137) + "...";
  }
  return stripped;
}
function BuyingGuidesConsumer() {
  const {
    posts,
    loading,
    error,
    refetch
  } = usePosts();
  if (loading) {
    return <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8">
        {[0, 1, 2].map(i => <div key={i} data-index={i} className="border border-border bg-card p-6 flex flex-col justify-between">
            <div>
              <div className="bg-muted/40 p-4 border border-border/50 mb-6">
                <AspectRatio ratio={16 / 10}>
                  <Skeleton className="w-full h-full rounded-none" />
                </AspectRatio>
              </div>
              <Skeleton className="h-4 w-28 rounded-none mb-3" />
              <Skeleton className="h-7 w-5/6 rounded-none mb-4" />
              <Skeleton className="h-4 w-full rounded-none mb-2" />
              <Skeleton className="h-4 w-4/5 rounded-none mb-8" />
            </div>
            <Skeleton className="h-10 w-32 rounded-none" />
          </div>)}
      </div>;
  }
  if (error) {
    return <div className="border border-border bg-card p-8 text-center max-w-xl mx-auto">
        <AlertCircle className="size-8 mx-auto text-destructive mb-3" />
        <h3 className="font-serif uppercase text-xl font-bold tracking-tight text-foreground mb-2">
          Unable to Load Resources
        </h3>
        <p className="text-sm text-muted-foreground mb-6">
          We experienced a connection issue loading the latest guides. Please try refreshing.
        </p>
        <Button onClick={() => refetch()} variant="outline" className="rounded-none border-foreground text-foreground hover:bg-foreground hover:text-background font-mono text-xs uppercase">
          Retry Connection
        </Button>
      </div>;
  }

  // Use fetched posts if available; if empty or incomplete, merge with curated fallback guides
  const displayItems = posts && posts.length > 0 ? posts.slice(0, 3).map((post, index) => {
    const fallback = FALLBACK_POSTS[index] || FALLBACK_POSTS[0];
    return {
      id: post.id || fallback.id,
      title: post.title || fallback.title,
      excerpt: cleanExcerpt(post.excerpt) || fallback.excerpt,
      link: post.link || fallback.link,
      image: post.featuredImage || fallback.featuredImage
    };
  }) : FALLBACK_POSTS;
  return <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8">
      {displayItems.map((item, i) => {
      const itemNumber = `GUIDE // 0${i + 1}`;
      return <article key={item.id} data-index={i} className="border border-border bg-card flex flex-col justify-between transition-colors hover:border-foreground/40 group">
            <div className="p-6 md:p-7">
              {/* Image Container with 16/10 aspect ratio and object-contain */}
              <div className="bg-muted/40 p-4 border border-border/60 mb-6">
                <AspectRatio ratio={16 / 10} className="flex items-center justify-center overflow-hidden">
                  <Link to={item.link} className="w-full h-full flex items-center justify-center focus:outline-none" aria-label={item.title}>
                    <img src={item.image} alt={item.title} loading="lazy" className="w-full h-full object-contain transition-transform duration-300 group-hover:scale-105" />
                  </Link>
                </AspectRatio>
              </div>

              {/* Categorization & Metadata */}
              <div className="flex items-center justify-between font-mono text-xs uppercase tracking-wider text-muted-foreground mb-3">
                <span>{itemNumber}</span>
                <span className="flex items-center gap-1 text-primary">
                  <BookOpen className="size-3.5" />
                  <span>Technical</span>
                </span>
              </div>

              {/* Title */}
              <h3 className="font-serif text-2xl lg:text-3xl font-bold uppercase tracking-tight text-foreground mb-3 leading-tight">
                <Link to={item.link} className="hover:text-primary transition-colors inline-block">
                  {item.title}
                </Link>
              </h3>

              {/* Excerpt */}
              <p className="text-sm text-muted-foreground leading-relaxed">
                {item.excerpt}
              </p>
            </div>

            {/* Action Bar */}
            <div className="p-6 pt-0 md:p-7 md:pt-0 border-t border-border/40 mt-6 pt-6 flex items-center justify-between">
              <Button asChild className="rounded-none bg-primary text-primary-foreground hover:bg-primary/90 font-mono text-xs uppercase tracking-wider h-10 px-5 group/btn">
                <Link to={item.link} className="inline-flex items-center gap-2">
                  <span>Read Guide</span>
                  <ArrowUpRight className="size-4 transition-transform group-hover/btn:translate-x-0.5 group-hover/btn:-translate-y-0.5" />
                </Link>
              </Button>
              <span className="font-mono text-xs text-muted-foreground uppercase">
                Free Resource
              </span>
            </div>
          </article>;
    })}
    </div>;
}
export default function BuyingGuidesAndResources() {
  const wp_query_params = {
    post_type: "post",
    include: [606, 612, 614],
    orderby: "include"
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section id="buyingguidesandresources" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-b border-border">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header Section */}
        <div className="max-w-3xl mb-12 md:mb-16">
          <div className="font-mono text-xs uppercase tracking-widest text-primary font-semibold mb-2">
            RESOURCES &amp; PROCUREMENT
          </div>
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-bold uppercase tracking-tight text-foreground mb-4">
            Mechanical Bull Buying Guides &amp; Resources
          </h2>
          <p className="text-base sm:text-lg text-muted-foreground leading-relaxed">
            Practical guides for buyers, venue operators and event businesses covering mechanical bull costs, rental business planning, setup and safety.
          </p>
        </div>

        {/* Dynamic Posts Consumer */}
        <PostsProvider wp_query={{
        post_type: "post",
        include: [606, 612, 614],
        orderby: "include",
        status: ["publish"]
      }}>
          <BuyingGuidesConsumer />
        </PostsProvider>
      </div>
    </section>;
}